import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import ButtonV2 from 'components/ButtonV2';
import Card from 'components/Card';
import ContentWrapper from 'components/ContentWrapper';
import DataTable, { CellPrimary, CellSecondary } from 'components/DataTable';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import Select from 'components/FormFields/Select';
import { DeleteConfirmModal } from 'components/FormModal';
import PageContainer from 'components/PageContainer';
import { HeaderContainer, HeaderTitle } from 'components/PageHeader';
import PageHeaderIcon from 'components/PageHeaderIcon';
import PageToolbar from 'components/PageToolbar';
import SearchInput from 'components/SearchInput';
import { useSearchInput, useUpdateQuery } from 'hooks/useSearchInput';
import GuestFormModal from 'pages/track/GuestList/GuestListEvents/components/GuestFormModal';
import PermissionModal from 'pages/track/PermissionModal/PermissionModal';
import { logDevError } from 'shared/alerts';
import { AppContext } from 'shared/AppContext';
import { formatPhoneNumber } from 'shared/formatters';
import { GET_GUESTS, useGetGuests, useDeleteGuest } from './gql';

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const GuestTypeBadge = styled.span`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: ${(props) =>
    props.type === 'year'
      ? props.theme.name === 'dark'
        ? 'rgba(99, 102, 241, 0.2)'
        : 'rgba(99, 102, 241, 0.1)'
      : props.theme.name === 'dark'
      ? 'rgba(34, 197, 94, 0.2)'
      : 'rgba(34, 197, 94, 0.1)'};
  color: ${(props) => (props.type === 'year' ? '#6366F1' : '#22C55E')};
`;

const TrackGuests = () => {
  const [guests, setGuests] = useState([]);
  const [count, setCount] = useState(0);
  const [showAddGuestModal, setShowAddGuestModal] = useState(false);
  const [editGuestId, setEditGuestId] = useState(null);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [guestToDelete, setGuestToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [guestTypeFilter, setGuestTypeFilter] = useState('');

  const { pathname } = useLocation();
  const isTrackAdmin = pathname.includes('/admin-track');
  const isEmployeeAdmin = pathname.includes('/admin-employee');
  const routePrefix = isTrackAdmin
    ? '/admin-track'
    : isEmployeeAdmin
    ? '/admin-employee'
    : '/admin';

  const {
    state: { capabilities },
  } = useContext(AppContext);

  const { input } = useSearchInput();
  const currentPage = input.page ? Number(input.page) : 1;
  const queryString = input.queryString || '';
  const updateQuery = useUpdateQuery();

  // Fetch guests data
  const { data, loading } = useGetGuests({ queryString });
  const deleteGuest = useDeleteGuest();

  useEffect(() => {
    if (data?.getGuestsV2) {
      let filteredGuests = data.getGuestsV2.results || [];

      // Apply guest type filter
      if (guestTypeFilter === 'year') {
        filteredGuests = filteredGuests.filter((guest) => !!guest.year);
      } else if (guestTypeFilter === 'event') {
        filteredGuests = filteredGuests.filter((guest) => !!guest.event_id);
      }

      setGuests(filteredGuests);
      setCount(data.getGuestsV2.count || 0);
    }
  }, [data, guestTypeFilter]);

  // Handle search
  const handleSearch = useCallback(
    (searchTerm) => {
      const currentQueryString = input.queryString || '';
      if (searchTerm !== currentQueryString) {
        updateQuery({ queryString: searchTerm, page: 1 });
      }
    },
    [updateQuery, input.queryString]
  );

  // Handle pagination
  const handlePageChange = (page) => {
    const totalPages = Math.ceil(count / 15);
    if (page < 1 || page > totalPages) return;
    updateQuery({ page });
  };

  // Handle add guest button
  const handleAddGuest = () => {
    if (!capabilities || capabilities?.add_guests) {
      setEditGuestId(null);
      setShowAddGuestModal(true);
    } else {
      setShowPermissionModal(true);
    }
  };

  // Handle edit guest
  const handleEditGuest = (guest) => {
    setEditGuestId(guest.id);
    setShowAddGuestModal(true);
  };

  // Handle delete click
  const handleDeleteClick = (guest) => {
    setGuestToDelete(guest);
    setShowDeleteModal(true);
  };

  // Handle delete confirm
  const handleDeleteConfirm = async () => {
    if (!guestToDelete) return;

    setIsDeleting(true);
    try {
      await deleteGuest({
        variables: { id: guestToDelete.id },
        update: (cache) => {
          const cachedData = cache.readQuery({
            query: GET_GUESTS,
            variables: { input: { queryString } },
          });
          if (cachedData) {
            const updatedResults = cachedData.getGuestsV2.results.filter(
              (guest) => guest.id !== guestToDelete.id
            );
            cache.writeQuery({
              query: GET_GUESTS,
              variables: { input: { queryString } },
              data: {
                ...cachedData,
                getGuestsV2: {
                  ...cachedData.getGuestsV2,
                  count: cachedData.getGuestsV2.count - 1,
                  results: updatedResults,
                },
              },
            });
          }
        },
      });
      toast.success('Guest deleted successfully');
      setShowDeleteModal(false);
      setGuestToDelete(null);
    } catch (error) {
      logDevError(error);
      toast.error('Error deleting guest');
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle guest type filter change
  const handleGuestTypeFilterChange = (e) => {
    setGuestTypeFilter(e.target.value);
  };

  // Guest type filter options
  const guestTypeOptions = [
    { label: 'All Guests', value: '' },
    { label: 'Full Season Guests', value: 'year' },
    { label: 'Single Event Guests', value: 'event' },
  ];

  // DataTable columns configuration
  const columns = [
    {
      field: 'name',
      header: 'Name',
      mobileTitle: true,
      render: (guest) => (
        <CellPrimary>{`${guest.last_name}, ${guest.first_name}`}</CellPrimary>
      ),
    },
    {
      field: 'phone_number',
      header: 'Phone Number',
      mobileSubtitle: true,
      render: (guest) => (
        <CellSecondary>{formatPhoneNumber(guest.phone_number)}</CellSecondary>
      ),
    },
    {
      field: 'type',
      header: 'Type',
      render: (guest) =>
        guest.year ? (
          <GuestTypeBadge type="year">Full Season</GuestTypeBadge>
        ) : guest.event_id ? (
          <GuestTypeBadge type="event">Single Event</GuestTypeBadge>
        ) : (
          '-'
        ),
    },
    {
      field: 'date',
      header: 'Date/Year',
      render: (guest) => guest.year || guest.event?.fullDate || '-',
    },
    {
      field: 'event',
      header: 'Event/Track',
      render: (guest) => guest.event?.name || guest.ownership?.label || '-',
    },
    {
      field: 'additional_guests',
      header: 'Additional',
      render: (guest) =>
        guest.additional_guests ? `+${guest.additional_guests}` : '0',
    },
  ];

  // DataTable actions configuration
  const actions = [
    {
      type: 'view',
      title: 'View Details',
      mobileLabel: 'View',
      to: (guest) => `${routePrefix}/guests/${guest.id}`,
    },
    {
      type: 'edit',
      title: 'Edit Guest',
      mobileLabel: 'Edit',
      onClick: handleEditGuest,
    },
    {
      type: 'delete',
      title: 'Delete Guest',
      mobileLabel: 'Delete',
      onClick: handleDeleteClick,
    },
  ];

  const totalPages = Math.ceil(count / 15);

  return (
    <EnhancedThemeProvider>
      <PageContainer>
        {/* Header Card */}
        <Card style={{ marginBottom: '30px' }}>
          <HeaderContainer>
            <PageHeaderIcon icon="users" />
            <HeaderTitle>All Guests</HeaderTitle>
          </HeaderContainer>
        </Card>

        {/* Content Card */}
        <Card noPadding>
          <ContentWrapper>
            {/* Page Header with Search */}
            <PageToolbar>
              <SearchInput
                onSearch={handleSearch}
                placeholder="Search guests..."
                debounceMs={500}
              />
              <div style={{ width: '180px', flexShrink: 0 }}>
                <Select
                  value={guestTypeFilter}
                  onChange={handleGuestTypeFilterChange}
                  options={guestTypeOptions}
                />
              </div>
              <ButtonGroup>
                <ButtonV2
                  onClick={handleAddGuest}
                  style={{ width: 'auto', flexShrink: 0 }}
                >
                  Add New Guest
                </ButtonV2>
                <ButtonV2
                  as={Link}
                  to={`${routePrefix}/guest-lists`}
                  style={{
                    width: 'auto',
                    flexShrink: 0,
                    textDecoration: 'none',
                  }}
                >
                  View Guest Lists
                </ButtonV2>
              </ButtonGroup>
            </PageToolbar>

            {/* DataTable */}
            <DataTable
              columns={columns}
              data={guests}
              loading={loading}
              keyField="id"
              emptyMessage="No guests found"
              emptySubtext="Try adjusting your search or add a new guest"
              actions={actions}
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </ContentWrapper>
        </Card>

        {/* Add/Edit Guest Modal */}
        <GuestFormModal
          isOpen={showAddGuestModal}
          onClose={() => {
            setShowAddGuestModal(false);
            setEditGuestId(null);
          }}
          guestId={editGuestId}
          queryString={queryString}
        />

        {/* Delete Confirmation Modal */}
        <DeleteConfirmModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setGuestToDelete(null);
          }}
          title="Delete Guest"
          message={`Are you sure you want to delete ${guestToDelete?.first_name} ${guestToDelete?.last_name}? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={handleDeleteConfirm}
          isDeleting={isDeleting}
        />

        {/* Permission Modal */}
        <PermissionModal
          isVisible={showPermissionModal}
          hideModal={() => setShowPermissionModal(false)}
          link={`${routePrefix}/settings/2`}
        />
      </PageContainer>
    </EnhancedThemeProvider>
  );
};

export default TrackGuests;
